import React, { useCallback, useEffect, useMemo, useState } from 'react';

// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import isEmpty from 'lodash/isEmpty';
import { Box, Select, Text } from '@solvhealth/jigsaw-v1';
import BookingTimeSelector from '../../../../../BookingTimeSelector';
import {
  BookCtaContainer,
  ModuleRoot,
  NextAvailablePickATimeToggle,
  NextButton,
  NoInPersonOnlineBookings,
  ReasonForVisit,
  RecentlyBooked,
  UserProfileSelector,
  VisitTypeToggle,
} from '../shared';
import {
  isBookable,
  isClosedLocation,
  isHomeHealthcareLocation,
  isSolvPartner,
  isTelemedAsapEligible,
  isTelemedLocation,
} from '../../../../../../core/util/location';
import { getFirstAvailableSlot } from '../../../../../../core/location/slots';
import { checkAppointmentDateQueryParam } from '../../utils';
import StyledTitle from '~/components/ClinicDetailPageFlexible/components/BookModule/components/StyledTitle';
import { ModuleVariant } from '~/components/ClinicDetailPageFlexible/components/BookModule';
import SelectTimeButton from '~/components/BookingTimeSelector/components/SelectTimeButton';
import dataTestIds from '~/test/test-ids';
import { useSolvSelector } from '~/reducers';
import EmergencyMessage from '~/components/EmergencyModal/EmergencyMessage';
import withAuthentication from '../../../../../../core/login/withAuthentication';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { useClinicStatus } from '~/hooks/useClinicStatus';

type Props = {
  associatedTelemedLocation?: any;
  className?: string;
  handleSelectedDate: (...args: any[]) => any;
  handleSelectSlot: (...args: any[]) => any;
  location: any;
  onSubmit: (...args: any[]) => any;
  selectedDate?: any;
  selectedSlot?: any;
  setCurrentlySelectedLocation: (value: LocationInterface) => any;
  currentlySelectedLocation?: any;
  setNewBookingLocationId: (...args: any[]) => any;
  variant?: ModuleVariant;
  // below props are for old booking flow
  accountSummary?: any;
  autoFocus?: boolean;
  errors?: any;
  fetchSuggestions: (...args: any[]) => any;
  form: formShape;
  handleChangeUserProfile: (...args: any[]) => any;
  handleRequestAppointmentClick: (...args: any[]) => any;
  isLoggedIn?: boolean;
  login?: any;
  newBooking?: any;
  onBlur: (...args: any[]) => any;
  onFieldChange: (...args: any[]) => any;
  reasonForVisit?: string;
  setShouldRenderSuggestions: (...args: any[]) => any;
  shouldRenderSuggestions?: boolean;
  showEmergencyCopy?: boolean;
  submitButtonDisabled?: boolean;
  suggestions?: any[];
  toggleEmergencyCopy: (...args: any[]) => any;
  visitTypeId?: string;
  setVisitTypeId: (visitTypeId: string) => void;
  visitTypes?: { id: string; name: string }[];
};

const Module = (props: Props) => {
  const {
    associatedTelemedLocation,
    className,
    handleSelectedDate,
    handleSelectSlot,
    location,
    onSubmit,
    selectedDate,
    selectedSlot,
    currentlySelectedLocation,
    setCurrentlySelectedLocation,
    setNewBookingLocationId,
    variant = ModuleVariant.DEFAULT,
    // below props are for old booking flow
    accountSummary,
    autoFocus,
    errors,
    fetchSuggestions,
    form,
    handleChangeUserProfile,
    handleRequestAppointmentClick,
    isLoggedIn,
    login,
    newBooking,
    onBlur,
    onFieldChange,
    reasonForVisit,
    setShouldRenderSuggestions,
    shouldRenderSuggestions,
    showEmergencyCopy,
    submitButtonDisabled,
    suggestions,
    toggleEmergencyCopy,
    visitTypes,
    visitTypeId,
    setVisitTypeId,
  } = props;

  const flags = useLDFlags();

  const viewport = useSolvSelector((state) => state.runtime.viewport);
  const hasAssociatedTelemedLocation = !isEmpty(associatedTelemedLocation);
  const associatedTelemedIsSelected =
    hasAssociatedTelemedLocation && currentlySelectedLocation?.id === associatedTelemedLocation?.id;
  const currentLocationWithSlotData = associatedTelemedIsSelected
    ? associatedTelemedLocation
    : location;

  const [lastSelectedLocationId, setLastSelectedLocationId] = useState('');
  const [nextAvailableSelected, setNextAvailableSelected] = useState(
    isTelemedAsapEligible(location)
  );

  const shouldShowNextAvailablePickATimeToggle = isTelemedAsapEligible(currentlySelectedLocation);
  const noInPersonOnlineBookings =
    !isBookable(location) && !isTelemedLocation(currentlySelectedLocation);
  const shouldShowBookingTimeSelector = !noInPersonOnlineBookings && !nextAvailableSelected;
  const locationHoursStatus = useClinicStatus(location)?.type;

  const selectDefaultSlot = useCallback(
    (l: LocationInterface) => {
      const firstAvailableSlot = getFirstAvailableSlot(null, l);
      const queryAppointment = checkAppointmentDateQueryParam(l);
      handleSelectSlot(queryAppointment || firstAvailableSlot);
    },
    [handleSelectSlot]
  );

  const handleLocationChange = useCallback(
    (l: LocationInterface) => {
      setCurrentlySelectedLocation(l);
      setNewBookingLocationId(l.id);
      setNextAvailableSelected(isTelemedAsapEligible(l));
      setLastSelectedLocationId(l.id);
      selectDefaultSlot(l);
    },
    [selectDefaultSlot, setCurrentlySelectedLocation, setNewBookingLocationId]
  );

  const handleNextAvailableClick = () => {
    const firstAvailableSlot = getFirstAvailableSlot(null, currentlySelectedLocation);
    handleSelectSlot(firstAvailableSlot);
    setNextAvailableSelected(true);
  };

  // This handles the selected location getting updated and mimics the logical behavior of switching tabs in the book module
  useEffect(() => {
    if (
      !isEmpty(currentLocationWithSlotData?.slots) &&
      (isEmpty(currentlySelectedLocation) ||
        lastSelectedLocationId !== currentlySelectedLocation.id)
    ) {
      handleLocationChange(currentLocationWithSlotData);
    }
  }, [
    currentLocationWithSlotData,
    currentlySelectedLocation,
    handleLocationChange,
    lastSelectedLocationId,
  ]);

  const moduleTitle = useMemo(() => {
    if (isHomeHealthcareLocation(location)) {
      return 'Request a visit';
    }

    if (!hasAssociatedTelemedLocation) {
      return isTelemedLocation(location) ? 'Book a video visit' : 'Book a clinic visit';
    }
    return 'Book a visit';
  }, [hasAssociatedTelemedLocation, location]);

  return (
    <ModuleRoot
      // @ts-expect-error ts-migrate(2322) FIXME: Property 'className' does not exist on type 'Intri... Remove this comment to see the full error message
      className={className}
      key={currentlySelectedLocation?.id?.toString()}
      onSubmit={onSubmit}
      title={moduleTitle}
      variant={variant}
    >
      <BookCtaContainer>
        <Box margin="auto" width="90%">
          {(hasAssociatedTelemedLocation || (visitTypes && visitTypes.length > 1)) && (
            <>
              <Text mb={2} mt={4} title="What type of care do you need?" variant="subtitle">
                What type of care do you need?
              </Text>
              {hasAssociatedTelemedLocation && (
                <VisitTypeToggle
                  handleInPersonClick={() => handleLocationChange(location)}
                  handleVideoVisitClick={() => handleLocationChange(associatedTelemedLocation)}
                  isVideoVisitSelected={associatedTelemedIsSelected}
                />
              )}

              {visitTypes && (
                <Box>
                  <Text mb={0.5} mt={2} title="What type of care do you need?" variant="subtitle2">
                    Select a reason for your visit:
                  </Text>
                  <Select
                    defaultValue={visitTypeId}
                    onChange={(event) => {
                      const selectedVisitType = visitTypes.find(
                        (visitType) => visitType.id === event.target.value
                      );
                      if (selectedVisitType?.id) {
                        setVisitTypeId(selectedVisitType.id);
                      }
                    }}
                  >
                    {visitTypes.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}
            </>
          )}
          {(hasAssociatedTelemedLocation || (visitTypes && visitTypes.length > 1)) && (
            <Text mb={3} mt={3} title="What type of care do you need?" variant="subtitle">
              When?
            </Text>
          )}
          <NextAvailablePickATimeToggle
            handleNextAvailableClick={handleNextAvailableClick}
            handlePickATimeClick={() => setNextAvailableSelected(false)}
            isPickATimeSelected={!nextAvailableSelected}
            visible={shouldShowNextAvailablePickATimeToggle}
          />

          {isHomeHealthcareLocation(location) && <StyledTitle title="Preferred visit time" />}
        </Box>
        <BookingTimeSelector
          handleSelectSlot={(...args) => {
            handleSelectSlot(args);
            if (flags.kis511H22022BookingFlow) {
              onSubmit();
            }
          }}
          isCTA={variant === ModuleVariant.MODAL || flags.kis511H22022BookingFlow}
          location={associatedTelemedIsSelected ? associatedTelemedLocation : location}
          selectedDate={selectedDate}
          selectedSlots={selectedSlot ? [selectedSlot] : []}
          setSelectedDate={handleSelectedDate}
          shouldSelectFirstSlotAsInitialValue
          viewport={viewport}
          visible={shouldShowBookingTimeSelector}
        />

        <NoInPersonOnlineBookings location={location} visible={noInPersonOnlineBookings} />

        {flags.kis511H22022BookingFlow ? (
          nextAvailableSelected && (
            <SelectTimeButton
              borderRadius="8px"
              buttonType="button"
              data-testid={dataTestIds.cdp.bookingFormSubmitBtn}
              defaultButtonCopy="Continue booking"
              locale="en"
              location={location}
              marginBottom="16px"
              marginTop="8px"
              onModalSubmit={onSubmit}
              selectedSlot={selectedSlot ? [selectedSlot] : []}
              shouldShowApptDateInButton={!nextAvailableSelected}
            />
          )
        ) : (
          <NextButton
            disabled={submitButtonDisabled}
            location={location}
            onClick={onSubmit}
            selectedSlot={selectedSlot}
            shouldShowApptDateInButton={!nextAvailableSelected}
            visible={!noInPersonOnlineBookings}
          />
        )}
      </BookCtaContainer>

      {!isClosedLocation(locationHoursStatus) && (
        <Box pb={2.5} pt={0.5} px="5%">
          {isSolvPartner(location) && <RecentlyBooked />}
        </Box>
      )}
    </ModuleRoot>
  );
};

export default withAuthentication(createForm()(Module));
